import { ChevronRight, Home } from "lucide-react";
import type { ReactNode } from "react";
import { createContext, useContext, useMemo } from "react";
import { generatePath, matchPath, useLocation } from "react-router-dom";
import { RouterLink } from "./navigation";

type BreadcrumbPageIndex = Record<
  string,
  {
    name?: string;
    title?: string;
  }
>;

interface PageIndexContextValue {
  pages: BreadcrumbPageIndex;
  homePageName?: string;
}

const PageIndexContext = createContext<PageIndexContextValue | null>(null);

export interface PageIndexProviderProps {
  pages: BreadcrumbPageIndex;
  /** Page name to use as the home/root crumb (e.g. "OrganisationsPage") */
  homePageName?: string;
  children: ReactNode;
}

export function PageIndexProvider({
  pages,
  homePageName,
  children,
}: PageIndexProviderProps) {
  const value = useMemo(() => ({ pages, homePageName }), [pages, homePageName]);
  return (
    <PageIndexContext.Provider value={value}>
      {children}
    </PageIndexContext.Provider>
  );
}

function usePageIndex() {
  return useContext(PageIndexContext);
}

type Crumb = {
  pattern: string;
  to: string;
  label: string;
};

function findBestMatch(
  pages: BreadcrumbPageIndex,
  pathname: string,
): {
  pattern: string | null;
  params: Record<string, string>;
} {
  let best: { pattern: string; params: Record<string, string> } | null = null;

  for (const pattern of Object.keys(pages)) {
    const match = matchPath({ path: pattern, end: true }, pathname);
    if (!match) continue;

    const params: Record<string, string> = {};
    for (const [key, value] of Object.entries(match.params)) {
      if (typeof value === "string") {
        params[key] = value;
      }
    }

    // Prefer the most specific match.
    if (!best || pattern.length > best.pattern.length) {
      best = { pattern, params };
    }
  }

  return best ?? { pattern: null, params: {} };
}

function findHomePattern(
  pages: BreadcrumbPageIndex,
  homePageName?: string,
): string | null {
  if (!homePageName) return null;
  for (const [pattern, entry] of Object.entries(pages)) {
    if (entry?.name === homePageName) {
      return pattern;
    }
  }
  return null;
}

function buildBreadcrumbPatterns(
  matchedPattern: string,
  allPatterns: Set<string>,
): string[] {
  const segments = matchedPattern.split("/").filter(Boolean);
  const prefixes: string[] = [];

  for (let i = 1; i <= segments.length; i += 1) {
    const candidate = `/${segments.slice(0, i).join("/")}`;
    if (allPatterns.has(candidate)) {
      prefixes.push(candidate);
    }
  }

  return prefixes;
}

function getCrumbLabel(
  pattern: string,
  pages: BreadcrumbPageIndex,
  params: Record<string, string>,
): string {
  const segments = pattern.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1];

  if (lastSegment?.startsWith(":")) {
    const paramName = lastSegment.slice(1);
    return params[paramName] ?? paramName;
  }

  return pages[pattern]?.title ?? lastSegment ?? pattern;
}

function buildCrumbs(
  pages: BreadcrumbPageIndex,
  matchedPattern: string,
  params: Record<string, string>,
  homePageName?: string,
): Crumb[] {
  const patternSet = new Set(Object.keys(pages));
  const homePattern = findHomePattern(pages, homePageName);

  const patterns = buildBreadcrumbPatterns(matchedPattern, patternSet);
  if (
    homePattern &&
    homePattern !== matchedPattern &&
    !patterns.includes(homePattern)
  ) {
    patterns.unshift(homePattern);
  }

  return patterns.map((pattern) => ({
    pattern,
    to: generatePath(pattern, params),
    label: getCrumbLabel(pattern, pages, params),
  }));
}

export function Breadcrumbs() {
  const ctx = usePageIndex();
  const location = useLocation();

  const { crumbs, homePattern } = useMemo(() => {
    if (!ctx?.pages) {
      return {
        crumbs: [] as Crumb[],
        homePattern: null as string | null,
      };
    }

    const match = findBestMatch(ctx.pages, location.pathname);
    if (!match.pattern) {
      return {
        crumbs: [] as Crumb[],
        homePattern: null as string | null,
      };
    }

    return {
      crumbs: buildCrumbs(
        ctx.pages,
        match.pattern,
        match.params,
        ctx.homePageName,
      ),
      homePattern: findHomePattern(ctx.pages, ctx.homePageName),
    };
  }, [ctx, location.pathname]);

  if (!ctx?.pages || crumbs.length === 0) return null;

  return (
    <nav className="flex items-center text-sm text-muted-foreground">
      {crumbs.map((crumb, idx) => {
        const isLast = idx === crumbs.length - 1;
        const isFirst = idx === 0;
        const showHomeIcon = isFirst && crumb.pattern === homePattern;

        const content = (
          <>
            {showHomeIcon && <Home className="h-4 w-4 mr-1" />}
            {crumb.label}
          </>
        );

        return (
          <span key={crumb.pattern} className="flex items-center">
            {isLast ? (
              <span
                className={[
                  showHomeIcon ? "flex items-center" : "",
                  "font-medium text-foreground",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {content}
              </span>
            ) : (
              <RouterLink
                to={crumb.to}
                className={[
                  showHomeIcon ? "flex items-center" : "",
                  "hover:text-primary transition-colors",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {content}
              </RouterLink>
            )}

            {!isLast && <ChevronRight className="h-4 w-4 mx-2" />}
          </span>
        );
      })}
    </nav>
  );
}

export default Breadcrumbs;
